import { useState } from 'react';
import { ArrowRight, CheckCircle2, Eye, EyeOff, KeyRound, PackageCheck, UserRound } from 'lucide-react';
import { api } from '../services/api';
import type { LoginResponse } from '../types';
import { Button, Field, Input } from '../components/UI';

export function LoginPage({ onLogin }: { onLogin: (result: LoginResponse) => void }) {
  const [employeeCode, setEmployeeCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await api.login(employeeCode.trim(), password);
      onLogin(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể đăng nhập.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-brand-panel">
        <div className="login-brand-top">
          <img src="/brand/bismart-logo-full.png" alt="Bi'S MART" />
          <span>Ứng dụng nội bộ</span>
        </div>
        <div className="login-hero">
          <span className="eyebrow"><PackageCheck size={18} /> HEAD OFFICE ASSET CONTROL</span>
          <h1>Nhập một lần.<br />Theo dõi xuyên suốt.</h1>
          <p>Tiếp nhận vật dụng từ chi nhánh, cấp phát theo phòng ban và theo dõi phân bổ ngay trên một quy trình thống nhất.</p>
          <div className="login-benefits">
            <div><CheckCircle2 /><span>Tự động sinh mã tài sản và phiếu giao nhận</span></div>
            <div><CheckCircle2 /><span>Checklist rõ ràng, giảm nhập liệu lặp lại</span></div>
            <div><CheckCircle2 /><span>Biết chính xác vật dụng đang ở đâu, ai đang giữ</span></div>
          </div>
        </div>
        <div className="brand-shape shape-one" />
        <div className="brand-shape shape-two" />
        <div className="brand-pattern" />
      </div>

      <div className="login-form-panel">
        <form className="login-card" onSubmit={submit}>
          <div className="login-card-heading">
            <span className="login-icon"><KeyRound size={24} /></span>
            <div>
              <h2>Đăng nhập hệ thống</h2>
              <p>Sử dụng mã nhân viên và mật khẩu được khai báo trên Google Sheet.</p>
            </div>
          </div>

          <Field label="Mã nhân viên" required>
            <div className="input-with-icon">
              <UserRound size={18} />
              <Input value={employeeCode} onChange={(event) => setEmployeeCode(event.target.value)} placeholder="Ví dụ: NV001" autoComplete="username" />
            </div>
          </Field>

          <Field label="Mật khẩu" required>
            <div className="input-with-icon">
              <KeyRound size={18} />
              <Input type={showPassword ? 'text' : 'password'} value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Nhập mật khẩu" autoComplete="current-password" />
              <button type="button" className="password-toggle" onClick={() => setShowPassword((value) => !value)} aria-label="Hiện hoặc ẩn mật khẩu">
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </Field>

          {error && <div className="form-alert">{error}</div>}

          <Button type="submit" className="login-submit" disabled={loading}>
            {loading ? 'Đang kiểm tra...' : <>Đăng nhập <ArrowRight size={18} /></>}
          </Button>

          {api.isMock && (
            <div className="demo-note">
              <b>Chế độ demo:</b> ADMIN01 / 123456
            </div>
          )}
        </form>
        <p className="login-footer">Bi’S MART · Nơi mẹ gửi trọn niềm tin</p>
      </div>
    </div>
  );
}
